import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./ChanelReplays.scss";
import { useTranslation } from "react-i18next";

import ReplayPreview from "./ReplayPreview";
import ChanelReplaysSensor from "../chanel/ChanelReplaysSensor";
import ChanelFilters from "./ChanelFilters";
import NoResults from "./NoResults";
import SelectionActionDialog from "./SelectionActionDialog";
import { ChanelSelectionContext } from "../chanel/ChanelSelection";
import { UserContext } from "../../api/user/userContext";

// the default of onSelectingChanged: one function for every render, so the effects that call it do not run again
const NO_SELECTION = () => {};

function ChanelReplays({
  data,
  params,
  startUrl,
  queryUrl,
  location,
  onDataUpdated = () => {},
  onVisibilityAllChanged = () => {},
  // v2-1574: the owner's selection, started from the channel header (ChanelApp); the search has none
  selecting = false,
  onSelectingChanged = NO_SELECTION
}) {
  const [loaded, setLoaded] = useState(false);
  const [ready, setReady] = useState(false);
  const { t } = useTranslation();
  const { user } = useContext(UserContext);

  // v2-1574: the replays ticked, to delete several at once
  const [selected, setSelected] = useState([]);
  // the action being confirmed on the selection: "delete", "public", "private", or none
  const [action, setAction] = useState(null);
  // the ids of the previews on screen, the first page and every page the sensors loaded since, for "select all"
  const shown = useRef([]);
  // bumped when a preview comes or goes during a selection, so that "select all" is worked out again: the sensors
  // load pages without re-rendering this list. Outside a selection nothing is bumped, or every page loaded by the
  // scroll would re-render every preview loaded before it; a selection that starts reads shown as it is.
  const [, setShownVersion] = useState(0);
  const selectingNow = useRef(selecting);
  selectingNow.current = selecting;
  const register = useCallback((id) => {
    if (shown.current.indexOf(id) === -1) {
      shown.current.push(id);
      if (selectingNow.current) {
        setShownVersion((v) => v + 1);
      }
    }
  }, []);
  const unregister = useCallback((id) => {
    shown.current = shown.current.filter((i) => i !== id);
    if (selectingNow.current) {
      setShownVersion((v) => v + 1);
    }
  }, []);
  const toggle = useCallback((id) => {
    setSelected((s) => (s.indexOf(id) === -1 ? [...s, id] : s.filter((i) => i !== id)));
  }, []);
  const selection = useMemo(
    () => ({ active: selecting, selected: selected, toggle: toggle, register: register, unregister: unregister }),
    [selecting, selected, toggle, register, unregister]
  );
  const stopSelecting = () => {
    setAction(null);
    onSelectingChanged(false);
  };
  // whoever ends the selection (cancel, a deletion, another tab or filter), nothing stays ticked for the next one
  useEffect(() => {
    if (!selecting) {
      setSelected([]);
    }
  }, [selecting]);
  const allSelected = selected.length > 0 && shown.current.every((id) => selected.indexOf(id) !== -1);

  useEffect(() => {
    //FETCH HAND DETAILS
    //console.log("chanelReplays effect");
    setReady(false);
    setLoaded(false);
    // v2-1574: another tab, or the list fetched again after a deletion: nothing stays selected out of sight
    onSelectingChanged(false);
    setSelected([]);
    let timer = setTimeout(() => {
      setLoaded(true);
    }, 100);
    let sensorTimer = setTimeout(() => {
      setReady(true);
    }, 1000);

    //FETCH HAND DETAILS
    return () => {
      //console.log("chanelReplays done");
      clearTimeout(timer);
      clearTimeout(sensorTimer);
    };
  }, [data, location.search, onSelectingChanged]);

  if (data !== null) {
    return (
      <ChanelSelectionContext.Provider value={onSelectingChanged === NO_SELECTION ? null : selection}>
      <div
        className={
          "chanel-sections-container d-flex flex-column " +
          (loaded ? "loaded" : "")
        }
      >
        <ChanelFilters startUrl={startUrl} filterValues={data}></ChanelFilters>
        {selecting ? (
          <div className="chanel-selection-bar d-flex flex-row flex-wrap align-items-center justify-content-center">
            <span className="chanel-selection-count">
              {t("chanelApp.selection.count", { count: selected.length })}
            </span>
            <button type="button" onClick={() => setSelected(allSelected ? [] : shown.current.slice())}>
              {t(allSelected ? "chanelApp.selection.none" : "chanelApp.selection.all")}
            </button>
            <button type="button" disabled={selected.length === 0} onClick={() => setAction("public")}>
              {t("chanelApp.selection.public")}
            </button>
            <button type="button" disabled={selected.length === 0} onClick={() => setAction("private")}>
              {t("chanelApp.selection.private")}
            </button>
            <button
              type="button"
              className="delete"
              disabled={selected.length === 0}
              onClick={() => setAction("delete")}
            >
              {t("chanelApp.selection.delete")}
            </button>
            <button type="button" onClick={stopSelecting}>
              {t("chanelApp.selection.cancel")}
            </button>
          </div>
        ) : (
          <></>
        )}
        <div className={"chanel-replays list d-flex flex-row" + (selecting ? " selecting" : "")}>
          {data.last.map((hand, index) => {
            return <ReplayPreview key={hand.id} hand={hand}></ReplayPreview>;
          })}
          {data.last.length === 0 ? <NoResults></NoResults> : <></>}
          {data.last.length === 10 && ready ? (
            <ChanelReplaysSensor
              params={params}
              queryUrl={queryUrl}
              location={location}
              page={1}
              onDataUpdated={onDataUpdated}
            ></ChanelReplaysSensor>
          ) : (
            <></>
          )}
        </div>
        <SelectionActionDialog
          action={action}
          ids={selected}
          token={user.token}
          handleClose={() => setAction(null)}
          onDone={() => {
            stopSelecting();
            onVisibilityAllChanged();
          }}
        ></SelectionActionDialog>
      </div>
      </ChanelSelectionContext.Provider>
    );
  } else {
    return <></>;
  }
}
export default ChanelReplays;
